import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DevicesApi, { AlertTicketPage, AlertTicketQuery, DeviceAlertSummary, DeviceIdentityParams } from "../apis/devicesApi";
import TicketPopupPagination from "./TicketPopupPagination";
import { VIOLET } from "../styles/brandTokens";

export default function DeviceAlertHistory({ identity, deviceName = "This device" }: { identity: DeviceIdentityParams; deviceName?: string }) {
  const { sourceType, sourceId, deviceId } = identity;
  const [summary, setSummary] = useState<DeviceAlertSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [selection, setSelection] = useState<(AlertTicketQuery & { label: string }) | null>(null);
  const [page, setPage] = useState<AlertTicketPage | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true, inFlight = false;
    setSummary(null); setError(null); setDisabled(false); setSelection(null);
    const load = async () => {
      if (inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const data = await DevicesApi.getAlertSummary({ sourceType, sourceId, deviceId });
        if (active) { setSummary(data); setError(null); setDisabled(false); }
      } catch (err: any) {
        if (active) {
          if (err?.status === 403) setDisabled(true);
          else setError("Unable to refresh alert history.");
        }
      } finally { inFlight = false; }
    };
    void load();
    const timer = window.setInterval(load, 30000);
    window.addEventListener("focus", load);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", load); };
  }, [sourceType, sourceId, deviceId, retry]);

  useEffect(() => {
    if (!selection) return;
    let active = true;
    setLoading(true); setPage(null); setPageError(null);
    const { label: _label, ...query } = selection;
    DevicesApi.getAlertTickets({ sourceType, sourceId, deviceId }, query)
      .then(data => { if (active) setPage(data); })
      .catch(() => { if (active) setPageError("Unable to load alert tickets."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [sourceType, sourceId, deviceId, selection, summary?.generated_at_ms]);

  if (disabled) return null;
  const countRow = (label: string, count: number, query: AlertTicketQuery) => (
    <Box key={label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
      <Typography variant="body2" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>{label}</Typography>
      <Button size="small" aria-label={`${label}: ${count} alert tickets`} onClick={() => setSelection({ ...query, label, offset: 0 })}
        sx={{ minWidth: 32, color: VIOLET.theoText, fontVariantNumeric: "tabular-nums" }}>{count}</Button>
    </Box>
  );
  return (
    <Box sx={{ mt: 2 }}>
      {error && <Alert severity="warning" action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>{error}</Alert>}
      {!summary && !error ? <CircularProgress size={18} aria-label="Loading alert history" /> : null}
      {summary && <>
        <Typography component="h3" sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Alert History</Typography>
        <Typography variant="caption" color="text.secondary">This device</Typography>
        {summary.history.map(({ days, count }) => countRow(days === 1 ? "Last 24 hours" : `Last ${days} days`, count, { scope: "device", days }))}
        <Typography component="h3" sx={{ fontSize: 13, fontWeight: 700, mt: 2, mb: 0.5 }}>Client Alerts</Typography>
        <Typography variant="caption" color="text.secondary">All client devices · Last 30 days</Typography>
        {summary.customer_id === null ? <Typography variant="body2" color="text.secondary">Client mapping unavailable.</Typography>
          : summary.customer_issues.length ? summary.customer_issues.map(({ issue_type, count }) => countRow(issue_type.toUpperCase(), count, { scope: "customer", days: 30, issue_type }))
          : <Typography variant="body2" color="text.secondary">No recorded client alerts.</Typography>}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Updated {new Date(summary.generated_at_ms).toLocaleTimeString()}</Typography>
      </>}
      <Dialog open={Boolean(selection)} onClose={() => setSelection(null)} fullWidth maxWidth="md" aria-labelledby="device-alert-tickets-title">
        <DialogTitle id="device-alert-tickets-title" sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>{selection?.label} · Alert tickets</Typography>
            <Typography variant="body2" color="text.secondary">{selection?.scope === "customer" ? "All client devices · Last 30 days" : `${deviceName} · ${selection?.days === 1 ? "Last 24 hours" : `Last ${selection?.days} days`}`}</Typography>
          </Box>
          <IconButton aria-label="Close alert tickets" onClick={() => setSelection(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "60vh", overflowY: "auto" }}>
          {loading ? <CircularProgress aria-label="Loading alert tickets" /> : pageError ? <Alert severity="warning" action={<Button onClick={() => setSelection(value => value && { ...value })}>Retry</Button>}>{pageError}</Alert> : <>
            <Table size="small" aria-label="Alert tickets" sx={{ tableLayout: "fixed", "& td, & th": { overflowWrap: "anywhere" } }}>
              <TableHead><TableRow><TableCell>Ticket</TableCell><TableCell>Subject</TableCell><TableCell>Device</TableCell><TableCell>Issue</TableCell><TableCell>Created</TableCell></TableRow></TableHead>
              <TableBody>{page?.tickets.map(ticket => <TableRow key={`${ticket.platform_id}:${ticket.ticket_id}`}>
                <TableCell><Link href={`/supervisor/tickets/${encodeURIComponent(ticket.ticket_id)}`} target="_blank" rel="noopener noreferrer">{ticket.display_id}</Link></TableCell>
                <TableCell>{ticket.title}</TableCell><TableCell>{ticket.host_name}</TableCell><TableCell>{ticket.issue_type.toUpperCase()}</TableCell>
                <TableCell>{new Date(ticket.created_ms).toLocaleDateString()}{", "}{new Date(ticket.created_ms).toLocaleTimeString(undefined, {
                  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
                })}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
            {page?.total === 0 && <Typography sx={{ py: 2 }}>No alert tickets in this period.</Typography>}
          </>}
        </DialogContent>
        <TicketPopupPagination offset={selection?.offset || 0} total={page?.total ?? null} loadedCount={page?.tickets.length || 0} loading={loading}
          onOffsetChange={offset => setSelection(value => value && { ...value, offset })} />
      </Dialog>
    </Box>
  );
}
